import { ForwardedRef, forwardRef, useEffect, useRef, useState } from 'react';
import Card from '../Card';
import { colors } from '../../../../colors';

interface Props {
  isActive: boolean;
  moveToStepFive: () => void;
  handleBackPress: () => void;
}

const PagesOnboarding = forwardRef(
  (
    { isActive, moveToStepFive, handleBackPress }: Props,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const scrollableRef = useRef<HTMLDivElement>(null);
    const [showOverlay, setShowOverlay] = useState(false);

    useEffect(() => {
      const handleScroll = () => {
        if (scrollableRef.current) {
          const { scrollTop, scrollHeight, clientHeight } =
            scrollableRef.current;
          // Show overlay if not at the bottom (more content below)
          const isMoreContentBelow = scrollTop + clientHeight < scrollHeight;
          setShowOverlay(isMoreContentBelow);
        }
      };

      const scrollableElement = scrollableRef.current;
      if (scrollableElement) {
        scrollableElement.addEventListener('scroll', handleScroll);
        // Initial check on mount
        handleScroll();
      }

      return () => {
        if (scrollableElement) {
          scrollableElement.removeEventListener('scroll', handleScroll);
        }
      };
    }, []);

    return (
      <Card
        index={4}
        title="Pages Overview"
        isActive={isActive}
        ref={ref}
        handleBackPress={handleBackPress}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 24,
            height: '100%',
          }}
        >
          <div
            ref={scrollableRef}
            style={{
              gap: 24,
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto',
              position: 'relative',
            }}
            className="no-scrollbar"
          >
            <div>
              <p
                style={{
                  margin: 0,
                  color: colors.mossGreen,
                  fontSize: 12,
                  textTransform: 'uppercase',
                  fontWeight: 'bold',
                }}
              >
                Overview page
              </p>
              <p
                style={{
                  margin: 0,
                  color: colors.white,
                  fontSize: 16,
                  fontWeight: 'bold',
                }}
              >
                As of now, you have access to a few pages. The overview page
                will give you a quick overview of your subscriptions, which ones
                are active and how much balance they have. They also provide
                some insightful charts to give you a quick glimpse how your
                balance is doing. From here, you can select a specific
                subscription to see some details.
              </p>
            </div>

            <div>
              <p
                style={{
                  margin: 0,
                  color: colors.mossGreen,
                  fontSize: 12,
                  textTransform: 'uppercase',
                  fontWeight: 'bold',
                }}
              >
                Discover page
              </p>
              <p
                style={{
                  margin: 0,
                  color: colors.white,
                  fontSize: 16,
                  fontWeight: 'bold',
                }}
              >
                The discover page gives you access to all available bots to
                subscribe to. You are only able to find bots that are available
                on your connected exchange accounts. Here you can select a bot
                and choose a balance to start with. You can change the balance
                of a subscriptions at any time.
              </p>
            </div>

            <div>
              <p
                style={{
                  margin: 0,
                  color: colors.mossGreen,
                  fontSize: 12,
                  textTransform: 'uppercase',
                  fontWeight: 'bold',
                }}
              >
                Subscription page
              </p>
              <p
                style={{
                  margin: 0,
                  color: colors.white,
                  fontSize: 16,
                  fontWeight: 'bold',
                }}
              >
                The subscription page gives you full insight of your balance and
                transactions. You can also adjust your balance here,
                pause/resume subscriptions and edit your exchange connections.
              </p>
            </div>
          </div>

          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'end',
              flex: '0 0 auto',
            }}
          >
            <button
              onClick={moveToStepFive}
              style={{
                backgroundColor: colors.green,
                border: `1px solid ${colors.green}`,
                borderRadius: 4,
                cursor: 'pointer',
                padding: 6,
                paddingLeft: 12,
                paddingRight: 12,
                color: colors.white,
                fontWeight: 'bold',
                textWrap: 'nowrap',
                fontSize: 16,
              }}
            >
              Continue
            </button>
          </div>

          {showOverlay && (
            <div
              style={{
                position: 'absolute',
                bottom: 70 /* Above the button (adjust based on button height) */,
                left: 0,
                right: 0,
                height: 100 /* Height of the fade effect */,
                background:
                  'linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.9))' /* Gradient fade */,
                pointerEvents: 'none' /* Allow clicks through overlay */,
              }}
            ></div>
          )}
        </div>
      </Card>
    );
  },
);

PagesOnboarding.displayName = 'PagesOnboarding';

export default PagesOnboarding;

/** Ask if the user wants to receive emails
 * Give quick info about credits system, pausing subscriptions, available pages
 */
